<template>
  <div class="p-6">
    <div class="flex items-center space-x-4 mb-6">
      <button @click="$router.push('/facility-services')" class="flex items-center text-gray-600 hover:text-gray-900">
        <svg class="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
        </svg>
        Back
      </button>
      <h2 class="text-2xl font-bold text-gray-800">Laboratory — Disciplines</h2>
    </div>

    <div v-if="loading" class="flex justify-center py-16">
      <svg class="animate-spin h-10 w-10 text-blue-600" fill="none" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"/>
      </svg>
    </div>

    <div v-else>
      <!-- Discipline cards -->
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <div
          v-for="disc in disciplines"
          :key="disc.name"
          @click="openDiscipline(disc)"
          class="cursor-pointer bg-white border-2 border-gray-200 hover:border-blue-500 rounded-lg p-5 transition-all shadow-sm hover:shadow-md"
        >
          <div class="flex items-center justify-between mb-3">
            <span class="text-2xl">{{ disciplineIcon(disc.name) }}</span>
            <span class="text-xs font-semibold bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
              {{ disc.service_count }} services
            </span>
          </div>
          <p class="font-semibold text-gray-800 capitalize">{{ disciplineLabel(disc.name) }}</p>
          <p class="text-xs text-gray-400 mt-1">Click to view services</p>
        </div>

        <!-- Add-unlinked fallback card -->
        <div
          @click="openUnlinked"
          class="cursor-pointer bg-gray-50 border-2 border-dashed border-gray-300 hover:border-gray-400 rounded-lg p-5 transition-all"
        >
          <div class="flex items-center justify-between mb-3">
            <span class="text-2xl">📋</span>
            <span class="text-xs font-semibold bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
              {{ unlinkedCount }} services
            </span>
          </div>
          <p class="font-semibold text-gray-500">Unclassified</p>
          <p class="text-xs text-gray-400 mt-1">Services without a discipline</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from '@/services/api'

const DISCIPLINE_META = {
  haematology:       { label: 'Haematology',        icon: '🩸' },
  biochemistry:      { label: 'Clinical Chemistry',  icon: '🧪' },
  immunoassay:       { label: 'Immunoassay / TFTs',  icon: '🔬' },
  coagulation:       { label: 'Coagulation',         icon: '🩺' },
  serology:          { label: 'Serology',            icon: '🦠' },
  hormones:          { label: 'Hormones',            icon: '⚗️' },
  tumour_markers:    { label: 'Tumour Markers',      icon: '🎗️' },
  microbiology:      { label: 'Microbiology',        icon: '🧫' },
  urinalysis:        { label: 'Urinalysis',          icon: '💧' },
  stool_parasitology:{ label: 'Stool & Parasitology',icon: '🔍' },
  csf_analysis:      { label: 'CSF Analysis',        icon: '🧠' },
  semen_analysis:    { label: 'Semen Analysis',      icon: '🔭' },
}

export default {
  name: 'LabDisciplinesView',
  data() {
    return {
      loading:       false,
      disciplines:   [],
      unlinkedCount: 0,
    }
  },
  computed: {
    departmentId()   { return this.$route.params.departmentId },
    departmentName() { return this.$route.params.departmentName },
  },
  mounted() {
    this.fetchDisciplines()
  },
  methods: {
    async fetchDisciplines() {
      this.loading = true
      try {
        const res = await apiClient.getDisciplinesForDepartment(this.departmentId)
        if (res.success) {
          this.disciplines   = res.data
          this.unlinkedCount = res.meta?.unlinked_count || 0
        }
      } catch (e) {
        this.$toast.error('Failed to load disciplines')
      } finally {
        this.loading = false
      }
    },
    disciplineLabel(name) {
      return DISCIPLINE_META[name]?.label || name.replace(/_/g, ' ')
    },
    disciplineIcon(name) {
      return DISCIPLINE_META[name]?.icon || '🔬'
    },
    openDiscipline(disc) {
      this.$router.push({
        name:   'DisciplineServices',
        params: { departmentId: this.departmentId, departmentName: this.departmentName, discipline: disc.name }
      })
    },
    openUnlinked() {
      this.$router.push({
        name:   'DisciplineServices',
        params: { departmentId: this.departmentId, departmentName: this.departmentName, discipline: '__unclassified__' }
      })
    },
  }
}
</script>
